import React, { useEffect, useState, useRef } from "react";
import { Loader2, Check, CheckCheck, CornerUpLeft, Forward, Ban, Play, RotateCw, Star, LifeBuoy, MessageCircle } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { openExternal } from "@/lib/externalLinks";
import { fmtTime } from "@/lib/dateLocale";
import { parseFirstUrl, parseVideoEmbed } from "@/lib/embeds";
import { isEmojiOnly, emojiCount, emojiFontSize } from "./emoji";
import { ReactionBar } from "./ReactionBar";
import { QuoteThumb, quoteLabel } from "./QuoteThumb";
import { ChatMedia } from "./ChatMedia";
import { AlbumGrid } from "./AlbumGrid";
import { SmartImg, SmartVideo } from "./SmartMedia";
import { ChatLinkPreview } from "./ChatLinkPreview";
import { RefCard } from "./RefCard";
import { hapticLight } from "@/lib/haptics";
import { SWIPE_REPLY_PX, swipeOffset } from "./swipeReply";
import { isDoubleTap } from "./doubleTap";

// One chat message bubble: long-press → reaction bar + reply; swipe right → quick reply; double tap → ❤️ ;
// tap an image → in-app lightbox; renders a quoted (replied-to) preview if any.
// Lot 728 — mémoïsée : la bulle ne se re-rend que si SES props changent (clavier, saisie, frappe du pair,
// sondage 15 s… ne re-dessinent plus toute la conversation) ; les handlers viennent de MessageList (stables).
const SWIPE_STYLE = { touchAction: "pan-y" };
const NO_TAP = "button, a, input, textarea, video, [data-no-tap]";
const ChatMessageInner = ({ m, mine, user, lang, t, chatTargetName, senderAvatar, peerLastRead, peerLastDelivered, reactTarget, isLastMine, onLongPress, onReply, onReact, onReactOpen, onReactionsOpen, onSendBig, onOpenMedia, onQuoteClick, onQuickForward, onRetry, selectMode, selected, onToggleSelect }) => {
  const navigate = useNavigate();
  // Entrée animée (glissé + fondu) seulement pour une bulle NOUVELLE : la mienne en cours d'envoi ou celle qui vient
  // d'arriver par le temps réel (`_fresh`) — jamais pour l'historique chargé ni au remplacement par la copie serveur.
  const [enterAnim] = useState(() => !!(m.pending || m._fresh));
  const [heartPop, setHeartPop] = useState(0);
  const tapRef = useRef({ at: 0, x: 0, y: 0 });
  const rx = m.reactions && Object.keys(m.reactions).length ? [...new Set(Object.values(m.reactions))] : [];
  const rxCount = m.reactions ? Object.keys(m.reactions).length : 0;
  const hasMedia = !!m.media_url;
  const isForwardable = hasMedia || /(https?:\/\/|www\.)/i.test(m.content || "");
  const linkUrl = !hasMedia ? (parseVideoEmbed(m.content)?.url || parseFirstUrl(m.content)) : null;
  const album = Array.isArray(m.media_group) && m.media_group.length > 1 ? m.media_group : null;
  // In-bubble upload progress ring (WhatsApp-style) while a media message is uploading.
  const uploading = !!m.pending && typeof m._uploadPct === "number" && m._uploadPct < 100;
  const RING = 2 * Math.PI * 18;
  const uploadOverlay = uploading ? (
    <div className="absolute inset-0 grid place-items-center pointer-events-none z-10" style={{ background: "rgba(0,0,0,0.4)" }} data-testid={`chat-upload-overlay-${m.id}`}>
      <div className="relative h-12 w-12">
        <svg className="h-12 w-12 -rotate-90" viewBox="0 0 44 44">
          <circle cx="22" cy="22" r="18" fill="none" stroke="rgba(255,255,255,0.3)" strokeWidth="4" />
          <circle cx="22" cy="22" r="18" fill="none" stroke="#fff" strokeWidth="4" strokeLinecap="round" strokeDasharray={RING} strokeDashoffset={RING * (1 - m._uploadPct / 100)} />
        </svg>
        <span className="absolute inset-0 grid place-items-center text-white text-[11px] font-bold" data-testid={`chat-upload-pct-${m.id}`}>{m._uploadPct}%</span>
      </div>
    </div>
  ) : null;
  const [showAllEmojis, setShowAllEmojis] = useState(false);
  useEffect(() => { if (reactTarget !== m.id) setShowAllEmojis(false); }, [reactTarget, m.id]);
  const [dx, setDx] = useState(0);
  const start = useRef(null);
  const dragging = useRef(false);
  const armed = useRef(false);
  const longPressed = useRef(false);
  const pressTimer = useRef(null);

  const onPointerDown = (e) => {
    start.current = { x: e.clientX, y: e.clientY, id: e.pointerId, el: e.currentTarget, ctl: !!e.target?.closest?.(NO_TAP) };
    dragging.current = false;
    armed.current = false;
    longPressed.current = false;
    clearTimeout(pressTimer.current);
    if (!selectMode) {
      pressTimer.current = setTimeout(() => { if (!dragging.current) { longPressed.current = true; onLongPress(m); } }, 450);
    }
  };
  const onPointerMove = (e) => {
    if (!start.current) return;
    const ddx = e.clientX - start.current.x;
    const ddy = e.clientY - start.current.y;
    if (Math.abs(ddx) > 6 || Math.abs(ddy) > 6) clearTimeout(pressTimer.current);
    if (selectMode || longPressed.current) return;
    // Lot 727 — glisser vers la DROITE pour répondre (façon WhatsApp). Le geste s'engage dès 8 px nettement horizontaux ;
    // le pointeur est alors capturé (le doigt peut sortir de la bulle) et `touch-action: pan-y` empêche la liste de
    // confisquer le geste (sinon Android envoie pointercancel au premier mouvement et le glissé ne se déclenche jamais).
    if (!dragging.current) {
      if (ddx > 8 && ddx > Math.abs(ddy) * 1.2) {
        dragging.current = true;
        try { start.current.el.setPointerCapture(start.current.id); } catch { /* noop */ }
      } else return;
    }
    const d = swipeOffset(ddx);
    setDx(d);
    if (!armed.current && ddx >= SWIPE_REPLY_PX) { armed.current = true; hapticLight(); }
  };
  const finish = (e) => {
    if (!start.current) return; // guard: pointerup + pointerleave/cancel both fire on native → run once
    clearTimeout(pressTimer.current);
    const s = start.current;
    const wasDrag = dragging.current, wasLong = longPressed.current;
    if (wasDrag && armed.current) onReply(m);
    try { s.el.releasePointerCapture(s.id); } catch { /* noop */ }
    // In selection mode the whole bubble is covered by a tap-overlay (below) that toggles it.
    setDx(0); start.current = null; dragging.current = false; armed.current = false;
    // Lot 728 — double tap sur la bulle (hors boutons / liens / lecteur) → ❤️ instantané, façon Messenger / Instagram.
    if (e?.type !== "pointerup" || wasDrag || wasLong || selectMode || s.ctl) return;
    const now = Date.now();
    if (isDoubleTap(tapRef.current, { at: now, x: s.x, y: s.y })) {
      tapRef.current = { at: 0, x: 0, y: 0 };
      onReact(m.id, "❤️");
      setHeartPop((k) => k + 1);
      hapticLight();
    } else {
      tapRef.current = { at: now, x: s.x, y: s.y };
    }
  };

  const bubbleStyle = { background: mine ? "var(--yalola-sent-bubble)" : "var(--yalola-received-bubble)", color: "var(--yalola-text)", border: mine ? "none" : "1px solid var(--yalola-border)", ...SWIPE_STYLE };
  // Render message text with clickable links (disabled in selection mode so the whole bubble toggles).
  const linkifyContent = (text) => {
    if (!text || selectMode) return text;
    const re = /(https?:\/\/[^\s]+)/gi;
    const out = [];
    let last = 0, mm, i = 0;
    while ((mm = re.exec(text)) !== null) {
      if (mm.index > last) out.push(text.slice(last, mm.index));
      const href = mm[0];
      out.push(
        <a key={`l${i++}`} href={href} target="_blank" rel="noopener noreferrer" onClick={(e) => e.stopPropagation()} onPointerDown={(e) => e.stopPropagation()} className="underline break-all" style={{ color: "#2563EB" }}>{href}</a>
      );
      last = mm.index + href.length;
    }
    if (last < text.length) out.push(text.slice(last));
    return out;
  };
  const read = mine && !!peerLastRead && m.created_at <= peerLastRead;
  // Trois états comme WhatsApp : ✓ envoyé · ✓✓ gris distribué (l'app du pair l'a reçu) · ✓✓ couleur lu.
  const delivered = mine && !read && (!!m.delivered_at || (!!peerLastDelivered && m.created_at <= peerLastDelivered));
  // WhatsApp-style time + read receipt. tone: "bubble" (inside coloured bubble),
  // "over" (on a dark image gradient) or "plain" (on the light chat background).
  const timeMeta = (tone = "bubble") => {
    const isDark = tone === "over";
    const textColor = isDark ? "rgba(255,255,255,0.95)" : "var(--yalola-text-muted)";
    const sentColor = isDark ? "rgba(255,255,255,0.8)" : "var(--yalola-text-muted)";
    const readColor = isDark ? "#FFE39A" : "var(--yalola-primary)";
    return (
      <span className="flex items-center gap-0.5 text-[10px] font-medium leading-none shrink-0" style={{ color: textColor }} data-testid={`chat-time-${m.id}`}>
        {m.starred && <Star size={10} fill={isDark ? "#FFE39A" : "#E0A63C"} style={{ color: isDark ? "#FFE39A" : "#E0A63C" }} data-testid={`chat-msg-starred-${m.id}`} />}
        {fmtTime(m.created_at, lang)}
        {mine && (m.pending
          ? <Loader2 size={12} className="animate-spin" style={{ color: sentColor }} data-testid={`chat-msg-pending-${m.id}`} />
          : m.failed
          ? <span className="text-[11px] font-bold leading-none" style={{ color: "#e11d48" }} data-testid={`chat-msg-failed-${m.id}`}>!</span>
          : (read ? <CheckCheck size={13} style={{ color: readColor }} data-testid={`chat-msg-read-${m.id}`} />
            : delivered ? <CheckCheck size={13} style={{ color: sentColor }} data-testid={`chat-msg-delivered-${m.id}`} />
            : <Check size={13} style={{ color: sentColor }} data-testid={`chat-msg-sent-${m.id}`} />))}
      </span>
    );
  };
  const rt = m.reply_to;
  const quoteEl = rt ? (
    <button
      onClick={(e) => { e.stopPropagation(); onQuoteClick(rt.id); }}
      className="flex items-center gap-1.5 w-full text-left mb-1 rounded-lg px-2 py-1 border-l-2 overflow-hidden"
      style={{ borderColor: "var(--yalola-primary)", background: mine ? "rgba(255,255,255,0.5)" : "var(--yalola-surface-alt)" }}
      data-testid={`chat-quote-${m.id}`}
    >
      <QuoteThumb mediaType={rt.media_type} url={rt.media_url} size={32} testId={`chat-quote-thumb-${m.id}`} />
      <div className="min-w-0">
        <div className="text-[10px] font-bold" style={{ color: "var(--yalola-primary)" }}>{rt.sender_id === user.id ? "Vous" : chatTargetName}</div>
        <div className="text-[11px] truncate" style={{ opacity: 0.85 }}>{quoteLabel(rt, t)}</div>
      </div>
    </button>
  ) : null;
  // Context card (story reply / listing / shared post) — sits above the quote, WhatsApp-style.
  const refEl = m.ref ? <RefCard card={m.ref} mine={mine} user={user} chatTargetName={chatTargetName} t={t || ((k, fb) => fb)} testId={`chat-ref-${m.id}`} /> : null;
  const emojiOnly = isEmojiOnly(m.content) && !rt && !m.ref;

  const handlers = {
    onPointerDown,
    onPointerMove,
    onPointerUp: finish,
    onPointerLeave: finish,
    onPointerCancel: finish,
    onContextMenu: (e) => { e.preventDefault(); if (!selectMode) onLongPress(m); },
  };

  const tr = t || ((k, fb) => fb);
  // Messenger-style delivery status, shown ONLY under the last outgoing bubble:
  // "Envoi…" while it uploads/sends → "Envoyé" once on the server → "Vu" once read.
  const statusText = (mine && isLastMine && !m.is_deleted && !m.failed)
    ? (m.pending ? tr("chat.sending", "Envoi…") : read ? tr("chat.seen", "Vu") : tr("chat.sent", "Envoyé"))
    : null;
  const forwardedTag = m.forwarded ? (
    <div className={`flex items-center gap-1 text-[11px] italic mb-0.5 ${mine ? "justify-end" : "justify-start"}`} style={{ color: "var(--yalola-text-muted)" }} data-testid={`chat-forwarded-${m.id}`}>
      <Forward size={12} /> {tr("chat.forwarded", "Transféré")}
    </div>
  ) : null;

  if (m.is_deleted) {
    return (
      <div className={`relative flex flex-col ${mine ? "items-end" : "items-start"}`}>
        <div className="rounded-2xl text-sm px-3 py-1.5 italic flex items-center gap-1.5" style={{ background: "var(--yalola-surface-alt)", color: "var(--yalola-text-muted)", border: "1px dashed var(--yalola-border)" }} data-testid={`chat-msg-deleted-${m.id}`}>
          <Ban size={13} /> {mine ? t("chat.you_deleted", "Vous avez supprimé ce message") : t("chat.msg_deleted", "Ce message a été supprimé")}
        </div>
        {m.created_at && (
          <span className="text-[10px] mt-0.5 px-1" style={{ color: "var(--yalola-text-muted)" }}>{fmtTime(m.created_at, lang)}</span>
        )}
      </div>
    );
  }

  return (
    <div
      className={`relative flex ${mine ? "flex-row-reverse" : "flex-row"} items-center ${selectMode ? "cursor-pointer" : ""} ${selected ? "rounded-lg" : ""}`}
      style={selected ? { background: "color-mix(in srgb, var(--yalola-primary) 20%, transparent)" } : undefined}
      data-testid={`chat-row-${m.id}`}
    >
      <div className={`relative flex flex-col min-w-0 ${mine ? "items-end" : "items-start"} ${rx.length ? "pb-3.5" : ""}`} style={{ maxWidth: "82%" }}>
      {dx > 0 && (
        <div
          className="absolute top-1/2 -translate-y-1/2 left-1 h-7 w-7 rounded-full grid place-items-center pointer-events-none"
          style={{ background: "var(--yalola-surface-alt)", color: armed.current ? "var(--yalola-primary)" : "var(--yalola-text-muted)", opacity: Math.min(1, dx / SWIPE_REPLY_PX), transform: `translateY(-50%) scale(${Math.min(1, 0.6 + dx / SWIPE_REPLY_PX)})` }}
          data-testid={`chat-swipe-reply-${m.id}`}
          data-armed={armed.current ? "1" : "0"}
        >
          <CornerUpLeft size={16} />
        </div>
      )}
      <div className={`relative max-w-full ${enterAnim ? "chat-bubble-in" : ""}`} style={{ transform: dx ? `translateX(${dx}px)` : undefined, transition: dx ? "none" : "transform 0.18s ease" }}>
        {heartPop > 0 && (
          <div key={heartPop} className="absolute inset-0 z-20 grid place-items-center pointer-events-none" data-testid={`chat-heart-pop-${m.id}`}>
            <span className="chat-heart-pop" onAnimationEnd={() => setHeartPop(0)}>❤️</span>
          </div>
        )}
        {selectMode && (
          <div
            className="absolute inset-0 z-30 cursor-pointer"
            onClick={(e) => { e.stopPropagation(); onToggleSelect(m); }}
            data-testid={`chat-select-overlay-${m.id}`}
          />
        )}
        {reactTarget === m.id && (
          <>
            {!selectMode && onReactOpen && (
              // Barre ouverte depuis le bouton « Réagir » (hors sélection) : un tap à côté la referme.
              <div className="fixed inset-0 z-[80]" onClick={() => onReactOpen(m)} data-testid={`reaction-bar-backdrop-${m.id}`} />
            )}
            <ReactionBar m={m} mine={mine} t={t} onReact={onReact} onSendBig={(e) => { onReactOpen(m); onSendBig(e); }} showAll={showAllEmojis} setShowAll={setShowAllEmojis} />
          </>
        )}
        {!selectMode && isForwardable && !m.is_deleted && (
          <button
            onClick={(e) => { e.stopPropagation(); onQuickForward && onQuickForward(m); }}
            onPointerDown={(e) => e.stopPropagation()}
            className={`absolute top-1/2 -translate-y-1/2 z-10 h-7 w-7 rounded-full grid place-items-center shadow-md transition-transform active:scale-90 ${mine ? "-left-9" : "-right-9"}`}
            style={{ background: "rgba(60,50,45,0.42)", color: "#fff", backdropFilter: "blur(2px)" }}
            data-testid={`chat-quickforward-${m.id}`}
            aria-label={t("chat.forward", "Transférer")}
          >
            <Forward size={14} />
          </button>
        )}
        {forwardedTag}
        {hasMedia ? (
          <div className="min-w-0 select-none" style={SWIPE_STYLE} data-testid={`chat-msg-${m.id}`} {...handlers}>
            {refEl}
            {quoteEl}
            {album ? (
              <div
                className="rounded-2xl overflow-hidden relative shadow-sm"
                style={{ maxWidth: 300, background: mine ? "var(--yalola-sent-bubble)" : "var(--yalola-received-bubble)", border: mine ? "none" : "1px solid var(--yalola-border)" }}
              >
                <AlbumGrid items={album} local={m._localGroup} onOpen={(i) => { if (!selectMode && !uploading && onOpenMedia) onOpenMedia(m, i); }} />
                {uploadOverlay}
                {m.content ? (
                  <div className="px-2.5 py-1.5" style={{ color: "var(--yalola-text)" }}>
                    <div className="text-sm whitespace-pre-wrap break-words">{m.content}</div>
                    <div className="flex justify-end mt-0.5">{timeMeta("bubble")}</div>
                  </div>
                ) : (
                  <div className="absolute bottom-0 inset-x-0 flex justify-end px-2 pb-1 pt-6 pointer-events-none" style={{ background: "linear-gradient(to top, rgba(0,0,0,0.55), rgba(0,0,0,0.12), transparent)" }}>
                    {timeMeta("over")}
                  </div>
                )}
              </div>
            ) : m.media_type === "audio" ? (
              <ChatMedia
                msg={m}
                mine={mine}
                timeEl={timeMeta("bubble")}
                sending={mine && !!m.pending && !m.failed}
                avatarUrl={senderAvatar}
                onReactOpen={onReactOpen && !selectMode ? () => onReactOpen(m) : null}
                reactActive={reactTarget === m.id}
              />
            ) : (
              <div
                className="rounded-2xl overflow-hidden relative shadow-sm"
                style={{ maxWidth: 300, background: mine ? "var(--yalola-sent-bubble)" : "var(--yalola-received-bubble)", border: mine ? "none" : "1px solid var(--yalola-border)" }}
              >
                {m.media_type === "video" ? (
                  <button
                    type="button"
                    onClick={() => { if (!selectMode && !uploading && onOpenMedia) onOpenMedia(m, 0); }}
                    className="relative block w-full cursor-pointer"
                    data-testid="chat-media-video"
                  >
                    <SmartVideo
                      remoteUrl={m.media_url}
                      localUrl={m._localPreview}
                      poster={m._poster || undefined}
                      className="w-full max-h-[340px] object-cover bg-black block pointer-events-none"
                    />
                    {!uploading && (
                      <span className="absolute inset-0 grid place-items-center pointer-events-none">
                        <span className="h-14 w-14 rounded-full bg-black/55 grid place-items-center"><Play size={26} className="text-white ml-0.5" fill="white" /></span>
                      </span>
                    )}
                  </button>
                ) : (
                  <SmartImg
                    remoteUrl={m.media_url}
                    localUrl={m._localPreview}
                    onClick={() => { if (!selectMode && !uploading && onOpenMedia) onOpenMedia(m, 0); }}
                    className="w-full h-auto max-h-[340px] object-cover block cursor-pointer"
                    data-testid="chat-media-image"
                  />
                )}
                {uploadOverlay}
                {m.content ? (
                  <div className="px-2.5 py-1.5" style={{ color: "var(--yalola-text)" }}>
                    <div className="text-sm whitespace-pre-wrap break-words">{m.content}</div>
                    <div className="flex justify-end mt-0.5">{timeMeta("bubble")}</div>
                  </div>
                ) : (
                  <div className="absolute bottom-0 inset-x-0 flex justify-end px-2 pb-1 pt-6" style={{ background: "linear-gradient(to top, rgba(0,0,0,0.55), rgba(0,0,0,0.12), transparent)" }}>
                    {timeMeta("over")}
                  </div>
                )}
              </div>
            )}
          </div>
        ) : emojiOnly ? (
          <div className={`select-none leading-none py-1 flex flex-col ${mine ? "items-end" : "items-start"}`} style={SWIPE_STYLE} data-testid={`chat-msg-${m.id}`} {...handlers}>
            <div style={{ fontSize: emojiFontSize(emojiCount(m.content)) }}>{m.content}</div>
            <div className="mt-1">{timeMeta("plain")}</div>
          </div>
        ) : (
          <div className="rounded-2xl text-sm px-3 py-1.5 select-none" style={bubbleStyle} data-testid={`chat-msg-${m.id}`} {...handlers}>
            {refEl}
            {quoteEl}
            {linkUrl && <ChatLinkPreview url={linkUrl} mine={mine} selectMode={selectMode} testId={`chat-link-preview-${m.id}`} />}
            {m.ref && isEmojiOnly(m.content)
              ? <div className="py-1 leading-none" style={{ fontSize: emojiFontSize(emojiCount(m.content)) }} data-testid={`chat-ref-emoji-${m.id}`}>{m.content}</div>
              : <div className="whitespace-pre-wrap break-words">{linkifyContent(m.content)}</div>}
            {m.cta?.link && (
              /* Lot 590 — bouton d'action du message (ex. réponse automatique → Aide & support) : navigation dans l'app, web comme mobile. */
              <button type="button" onClick={(e) => { e.stopPropagation(); if (!selectMode) navigate(m.cta.link); }} onPointerDown={(e) => e.stopPropagation()}
                className="mt-2 w-full rounded-full px-3 py-2 text-sm font-semibold text-white flex items-center justify-center gap-1.5" style={{ background: "var(--yalola-primary)" }} data-testid={`chat-cta-${m.id}`}>
                <LifeBuoy size={15} /> {m.cta.label || t?.("chat.support_autoreply_cta", "Ouvrir Aide & support") || "Ouvrir Aide & support"}
              </button>
            )}
            {m.cta_whatsapp?.url && (
              /* Lot 591 — 2e bouton optionnel : WhatsApp support (navigateur in-app sur mobile, nouvel onglet sur le web). */
              <button type="button" onClick={(e) => { e.stopPropagation(); if (!selectMode) openExternal(m.cta_whatsapp.url).catch(() => {}); }} onPointerDown={(e) => e.stopPropagation()}
                className="mt-1.5 w-full rounded-full px-3 py-2 text-sm font-semibold text-white flex items-center justify-center gap-1.5" style={{ background: "#25D366" }} data-testid={`chat-cta-whatsapp-${m.id}`}>
                <MessageCircle size={15} /> {m.cta_whatsapp.label || t?.("chat.support_autoreply_whatsapp", "Écrire sur WhatsApp") || "Écrire sur WhatsApp"}
              </button>
            )}
            {m.auto_reply && <div className="text-[10px] mt-1 opacity-60" data-testid={`chat-auto-reply-${m.id}`}>{t?.("chat.auto_reply_badge", "Réponse automatique") || "Réponse automatique"}</div>}
            <div className="flex justify-end mt-0.5">{timeMeta("bubble")}</div>
          </div>
        )}
        {rx.length > 0 && (
          /* Lot 729 — pastille des réactions (emojis + nombre si ≥ 2), tapable → feuille « Qui a réagi » ; ne déclenche ni glissé ni double tap. */
          <button
            type="button"
            key={rx.join("") + rxCount}
            onClick={(e) => { e.stopPropagation(); if (!selectMode) onReactionsOpen(m); }}
            onPointerDown={(e) => e.stopPropagation()}
            onPointerUp={(e) => e.stopPropagation()}
            className="absolute -bottom-3.5 left-1 z-20 flex items-center gap-0.5 rounded-full px-1.5 py-0.5 shadow-sm chat-react-pop transition-transform active:scale-95"
            style={{ background: "var(--yalola-surface)", border: "1px solid var(--yalola-border)" }}
            data-testid={`reactions-${m.id}`}
            aria-label={t("chat.reactions_title", "Réactions")}
          >
            {rx.map((e, i) => (
              <span key={i} className="text-base leading-none">{e}</span>
            ))}
            {rxCount > 1 && <span className="text-[11px] font-bold leading-none ml-0.5" style={{ color: "var(--yalola-text-muted)" }} data-testid={`reactions-count-${m.id}`}>{rxCount}</span>}
          </button>
        )}
      </div>
      {statusText && (
        <span className="mt-0.5 px-1 text-[10px] font-medium leading-none" style={{ color: "var(--yalola-text-muted)" }} data-testid={`chat-status-${m.id}`}>{statusText}</span>
      )}
      {mine && m.failed && (
        <button
          onClick={(e) => { e.stopPropagation(); onRetry && onRetry(m); }}
          onPointerDown={(e) => e.stopPropagation()}
          className="mt-1 flex items-center gap-1 text-[11px] font-semibold px-2.5 py-1 rounded-full transition-transform active:scale-90"
          style={{ color: "#e11d48", background: "rgba(225,29,72,0.12)" }}
          data-testid={`chat-retry-${m.id}`}
        >
          <RotateCw size={12} /> {tr("chat.retry", "Réessayer")}
        </button>
      )}
    </div>
    </div>
  );
};

export const ChatMessage = React.memo(ChatMessageInner);
